import { DateTimeStyle, getDateTimeFormatter, getRelativeTimeFormatter } from "./Intl";
import { getType, ObjectType } from "./Object";
import { isNullOrWhiteSpace, isString } from "./String";

export function dateFromString(date: unknown): Date {
  if (!isString(date) || isNullOrWhiteSpace(date)) {
    return null;
  }

  return new Date(Date.parse(date));
}

export function isDate(value: unknown): value is Date {
  return getType(value) === ObjectType.Date;
}

export function formatDate(
  locale: string,
  date: Date,
  dateStyle: DateTimeStyle = `medium`,
  timeStyle: DateTimeStyle = `short`
): string {
  if (date == null) {
    return null;
  }

  return getDateTimeFormatter(locale, dateStyle, timeStyle).format(date);
}

function formatPart(
  locale: string,
  partType: Intl.DateTimeFormatPartTypes,
  date: Date,
  dateStyle: DateTimeStyle = `medium`,
  timeStyle: DateTimeStyle = `short`
): string {
  if (date == null) {
    return null;
  }

  const parts = getDateTimeFormatter(locale, dateStyle, timeStyle).formatToParts(date);
  return parts.find((part) => part.type === partType)?.value;
}

export function is12HourFormat(locale: string): boolean {
  const hour = formatPart(locale, `hour`, new Date(2022, 3, 1, 15), `short`, `short`);
  return hour?.length === 1 ?? false;
}

export function formatDay(
  locale: string,
  date: Date,
  dateStyle: DateTimeStyle = `medium`,
  timeStyle: DateTimeStyle = `short`
): string {
  return formatPart(locale, `day`, date, dateStyle, timeStyle);
}

export function formatMonth(
  locale: string,
  date: Date,
  dateStyle: DateTimeStyle = `medium`,
  timeStyle: DateTimeStyle = `short`
): string {
  return formatPart(locale, `month`, date, dateStyle, timeStyle);
}

export function formatYear(
  locale: string,
  date: Date,
  dateStyle: DateTimeStyle = `medium`,
  timeStyle: DateTimeStyle = `short`
): string {
  return formatPart(locale, `year`, date, dateStyle, timeStyle);
}

const MS_PER_SECONDS = 1000;
const MS_PER_MINUTE = MS_PER_SECONDS * 60;
const MS_PER_HOUR = MS_PER_MINUTE * 60;
const MS_PER_DAY = MS_PER_HOUR * 24;
const MS_PER_WEEK = MS_PER_DAY * 7;
const MS_PER_MONTH = MS_PER_DAY * 30;
const MS_PER_YEAR = MS_PER_MONTH * 12;

export function formatTimeRelative(locale: string, date: Date): string {
  return getRelativeTimeFormatter(locale).format(...getTimeFormatArgs(date));
}

function getTimeFormatArgs(date: Date): [number, Intl.RelativeTimeFormatUnit] {
  const deltaMs = date.getTime() - new Date().getTime();
  const absoluteDeltaMs = Math.abs(deltaMs);

  let value: number = null;
  let unit: Intl.RelativeTimeFormatUnit;

  if (absoluteDeltaMs <= MS_PER_YEAR) {
    value = deltaMs / MS_PER_MONTH;
    unit = `months`;
  }
  if (absoluteDeltaMs <= MS_PER_MONTH) {
    value = deltaMs / MS_PER_WEEK;
    unit = `weeks`;
  }
  if (absoluteDeltaMs <= MS_PER_WEEK) {
    value = deltaMs / MS_PER_DAY;
    unit = `days`;
  }
  if (absoluteDeltaMs <= MS_PER_DAY) {
    value = deltaMs / MS_PER_HOUR;
    unit = `hours`;
  }
  if (absoluteDeltaMs <= MS_PER_HOUR) {
    value = deltaMs / MS_PER_MINUTE;
    unit = `minutes`;
  }
  if (absoluteDeltaMs <= MS_PER_MINUTE) {
    value = deltaMs / MS_PER_SECONDS;
    unit = `seconds`;
  }

  if (value == null) {
    value = deltaMs / MS_PER_YEAR;
    unit = `years`;
  }

  return [Math.round(value), unit];
}

export function utcNowAsISO(): string {
  return new Date().toISOString();
}

export function isNeverDate(dateString: string): boolean {
  return isNullOrWhiteSpace(dateString) || dateString === `9000-01-01T00:00:00Z`;
}
